import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { usePermission, useIsOwner, useEffectivePermissions } from '../roles/RoleGuard';
import Shell from '../dashboard/Shell';
import Modal from '../../components/ui/Modal';
import FeatureGate from '../../components/FeatureGate';
import {
  Users, Shield, RefreshCw, ClipboardList, UserPlus, Copy, Trash2,
  Eye, Edit3, Crown, Plus, Send, CheckCircle2, XCircle, Clock, Mail,
  AlertTriangle, Loader2, ChevronDown, ChevronRight, Ban, Lock, Unlock,
  Archive, LogIn, LogOut, UserCheck, UserX, Package, ShoppingCart,
  BarChart3, Megaphone, Briefcase, Settings, DollarSign, Store,
} from 'lucide-react';
import {
  fetchRoles, createRole, updateRole, deleteRole, updateRolePermissions,
  cloneRole, initializeDefaultRoles,
  selectAllRoles, selectRolesStatus,
} from '../roles/rolesSlice';
import {
  fetchPermissionsByCategory, fetchMyPermissions, seedPermissions,
  selectAllPermissions, selectPermissionsByCategory, selectPermissionCategories,
} from '../roles/permissionsSlice';
import {
  fetchGrantedDelegations, fetchReceivedDelegations, fetchPendingDelegations,
  fetchDelegablePermissions, createDelegation, approveDelegation,
  rejectDelegation, revokeDelegation,
  selectGrantedDelegations, selectReceivedDelegations, selectPendingDelegations,
  selectDelegablePermissions, selectDelegationsStatus,
} from './delegationsSlice';
import './TeamManagement.css';

const TABS = {
  MEMBERS: 'members',
  ROLES: 'roles',
  DELEGATIONS: 'delegations',
  ACTIVITY: 'activity',
};

const ROLE_COLORS = {
  Owner: '#DC2626',
  Manager: '#8B5CF6',
  'Inventory Manager': '#3B82F6',
  'Sales Manager': '#10B981',
  'Marketing Manager': '#F59E0B',
  Cashier: '#06B6D4',
  Viewer: '#6B7280',
};
const getColorForRole = (roleName) => ROLE_COLORS[roleName] || '#0D7463';

const CATEGORY_ICONS = {
  Products: Package,
  Orders: ShoppingCart,
  Customers: Users,
  Inventory: ClipboardList,
  Analytics: BarChart3,
  Marketing: Megaphone,
  Team: Briefcase,
  Settings: Settings,
  Finance: DollarSign,
  POS: Store,
};

// Activity-log action → Lucide icon (was emoji 📧 ✅ 🔒 etc.)
const ACTIVITY_ICONS = {
  'member.invited': Mail, 'member.joined': UserCheck, 'member.suspended': UserX,
  'member.reactivated': UserPlus, 'member.archived': Archive,
  'role.assigned': Shield, 'role.changed': RefreshCw,
  'permission.granted': Unlock, 'permission.revoked': Lock,
  'override.added': Plus, 'override.removed': Trash2,
  'delegation.created': Send, 'delegation.approved': CheckCircle2,
  'delegation.rejected': XCircle, 'delegation.revoked': Ban,
  'delegation.expired': Clock,
  'access.login': LogIn, 'access.logout': LogOut, 'access.denied': Ban,
  MEMBER_INVITED: Mail, MEMBER_JOINED: UserCheck, MEMBER_REMOVED: Ban,
  ROLE_CHANGED: RefreshCw, ROLE_CREATED: Plus, ROLE_UPDATED: Edit3,
  ROLE_DELETED: Trash2,
  PERMISSION_GRANTED: Unlock, PERMISSION_REVOKED: Lock,
  DELEGATION_CREATED: Send, DELEGATION_APPROVED: CheckCircle2,
  DELEGATION_REJECTED: XCircle, DELEGATION_REVOKED: Ban,
};
const iconForActivity = (action) => ACTIVITY_ICONS[action] || ClipboardList;

const ROLE_PALETTE = ['#0D7463', '#8B5CF6', '#3B82F6', '#10B981', '#F59E0B', '#06B6D4', '#EC4899', '#6B7280'];

export default function TeamManagement() {
  const dispatch = useDispatch();
  const { getAxios, user } = useSecureAuth();
  const isOwner = useIsOwner();
  const { permissions: myPermissions } = useEffectivePermissions(); // eslint-disable-line no-unused-vars

  const canManageTeam   = usePermission('team:edit')   || isOwner;
  const canInviteMembers = usePermission('team:invite') || isOwner;
  const canManageRoles  = usePermission('team:manage') || isOwner;

  const roles = useSelector(selectAllRoles);
  const rolesStatus = useSelector(selectRolesStatus);
  const allPermissions = useSelector(selectAllPermissions); // eslint-disable-line no-unused-vars
  const permissionsByCategory = useSelector(selectPermissionsByCategory);
  const permissionCategories = useSelector(selectPermissionCategories); // eslint-disable-line no-unused-vars
  const grantedDelegations = useSelector(selectGrantedDelegations);
  const receivedDelegations = useSelector(selectReceivedDelegations);
  const pendingDelegations = useSelector(selectPendingDelegations);
  const delegablePermissions = useSelector(selectDelegablePermissions);
  const delegationsStatus = useSelector(selectDelegationsStatus);

  const [activeTab, setActiveTab] = useState(TABS.MEMBERS);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [teamLimits, setTeamLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modals
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showCreateRoleModal, setShowCreateRoleModal] = useState(false);
  const [showEditPermissionsModal, setShowEditPermissionsModal] = useState(false);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [showMemberDetailsModal, setShowMemberDetailsModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  // Confirmation modal (replaces old createPortal DeleteConfirmModal).
  // `confirm` is null or { title, message, confirmText, type, onConfirm }.
  const [confirm, setConfirm] = useState(null);

  // Form state
  const [inviteForm, setInviteForm] = useState({ email: '', roleId: '' });
  const [roleForm, setRoleForm] = useState({ name: '', description: '', color: '#0D7463' });
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [delegationForm, setDelegationForm] = useState({
    delegateToId: '', permissionIds: [], reason: '', expiresAt: '', requiresApproval: false,
  });

  useEffect(() => { loadAllData(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { dispatch(fetchMyPermissions()); }, [dispatch]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      dispatch(fetchRoles({ includePermissions: true }));
      dispatch(fetchPermissionsByCategory());
      dispatch(fetchPendingDelegations());
      dispatch(fetchGrantedDelegations({}));
      dispatch(fetchReceivedDelegations({}));
      dispatch(fetchDelegablePermissions());

      const axiosInstance = getAxios();
      const [membersRes, invitesRes] = await Promise.all([
        axiosInstance.get('/team'),
        axiosInstance.get('/team/invites'),
      ]);

      if (membersRes.data.success) {
        setTeamMembers(membersRes.data.team || []);
        if (membersRes.data.limits) setTeamLimits(membersRes.data.limits);
      }
      if (invitesRes.data.success) {
        setPendingInvites(invitesRes.data.invites || []);
      }

      try {
        const activityRes = await axiosInstance.get('/team/activity?limit=50');
        if (activityRes.data.success) setActivityLogs(activityRes.data.logs || []);
      } catch (e) {
        console.log('Activity logs not available:', e.message);
      }
    } catch (err) {
      console.error('Failed to load team data:', err);
      setError('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  // Toast-style auto-clear
  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  // Wrapped confirmation helper. confirm callback closes the dialog
  // first, then runs the action — keeps the UI responsive.
  const askConfirm = (title, message, confirmText, type, onConfirm) => {
    setConfirm({
      title, message, confirmText, type,
      onConfirm: async () => {
        setConfirm(null);
        await onConfirm();
      },
    });
  };

  /* ─── Member handlers ───────────────────────────────────────── */
  const handleInviteMember = async (e) => {
    e?.preventDefault?.();
    try {
      setLoading(true);
      setError('');
      const axiosInstance = getAxios();
      const selectedRoleObj = roles.find((r) => r.id === inviteForm.roleId);
      const r = await axiosInstance.post('/team/invite', {
        email: inviteForm.email,
        role: selectedRoleObj?.name || 'VIEWER',
        roleId: inviteForm.roleId,
      });
      if (r.data.success) {
        flash(`Invitation sent to ${inviteForm.email}`);
        setShowInviteModal(false);
        setInviteForm({ email: '', roleId: '' });
        loadAllData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateMemberRole = async (e) => {
    e?.preventDefault?.();
    if (!selectedMember) return;
    try {
      setLoading(true);
      setError('');
      const axiosInstance = getAxios();
      const r = await axiosInstance.put(`/team/${selectedMember.id}/role`, {
        roleId: inviteForm.roleId,
      });
      if (r.data.success) {
        flash('Role updated');
        setShowEditRoleModal(false);
        setSelectedMember(null);
        setInviteForm({ email: '', roleId: '' });
        loadAllData();
        dispatch(fetchMyPermissions());
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = (memberId, memberEmail) => {
    askConfirm(
      'Remove team member',
      `Remove ${memberEmail} from your team? This can't be undone.`,
      'Remove', 'danger',
      async () => {
        try {
          setLoading(true);
          const r = await getAxios().delete(`/team/${memberId}`);
          if (r.data.success) { flash('Team member removed'); loadAllData(); }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to remove team member');
        } finally { setLoading(false); }
      }
    );
  };

  const handleCancelInvite = (inviteId) => {
    askConfirm(
      'Cancel invitation',
      'The invite link will stop working. Cancel this invitation?',
      'Cancel invitation', 'warn',
      async () => {
        try {
          setLoading(true);
          const r = await getAxios().delete(`/team/invites/${inviteId}`);
          if (r.data.success) { flash('Invitation cancelled'); loadAllData(); }
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to cancel invitation');
        } finally { setLoading(false); }
      }
    );
  };

  /* ─── Role handlers ─────────────────────────────────────────── */
  const handleCreateRole = async (e) => {
    e?.preventDefault?.();
    try {
      await dispatch(createRole({
        name: roleForm.name,
        description: roleForm.description,
        color: roleForm.color,
        permissionIds: selectedPermissions,
      })).unwrap();
      flash('Role created');
      setShowCreateRoleModal(false);
      setRoleForm({ name: '', description: '', color: '#0D7463' });
      setSelectedPermissions([]);
      dispatch(fetchRoles({ includePermissions: true }));
    } catch (err) {
      setError(err.error || 'Failed to create role');
    }
  };

  const handleUpdateRolePermissions = async () => {
    if (!selectedRole) return;
    try {
      // First update name/description/color if they changed
      if (
        roleForm.name !== selectedRole.name ||
        roleForm.description !== (selectedRole.description || '') ||
        roleForm.color !== (selectedRole.color || '#0D7463')
      ) {
        await dispatch(updateRole({
          roleId: selectedRole.id,
          roleData: {
            name: roleForm.name,
            description: roleForm.description,
            color: roleForm.color,
          },
        })).unwrap();
      }
      await dispatch(updateRolePermissions({
        roleId: selectedRole.id,
        permissionIds: selectedPermissions,
      })).unwrap();
      flash('Role updated');
      setShowEditPermissionsModal(false);
      setSelectedRole(null);
      setSelectedPermissions([]);
      dispatch(fetchRoles({ includePermissions: true }));
      dispatch(fetchMyPermissions());
    } catch (err) {
      setError(err.error || 'Failed to update role');
    }
  };

  const handleDeleteRole = (role) => {
    if (role.isSystem) { setError('Cannot delete system roles'); return; }
    askConfirm(
      'Delete role',
      `Delete the "${role.name}" role? Members with this role will need to be reassigned.`,
      'Delete', 'danger',
      async () => {
        try {
          await dispatch(deleteRole({ roleId: role.id })).unwrap();
          flash('Role deleted');
          dispatch(fetchRoles({ includePermissions: true }));
        } catch (err) {
          setError(err.error || 'Failed to delete role');
        }
      }
    );
  };

  const handleCloneRole = async (role) => {
    try {
      await dispatch(cloneRole({ roleId: role.id, newName: `${role.name} (Copy)` })).unwrap();
      flash('Role cloned');
      dispatch(fetchRoles({ includePermissions: true }));
    } catch (err) {
      setError(err.error || 'Failed to clone role');
    }
  };

  /* ─── Delegation handlers ───────────────────────────────────── */
  const handleCreateDelegation = async (e) => {
    e?.preventDefault?.();
    try {
      await dispatch(createDelegation(delegationForm)).unwrap();
      flash('Delegation created');
      setShowDelegateModal(false);
      setDelegationForm({ delegateToId: '', permissionIds: [], reason: '', expiresAt: '', requiresApproval: false });
      dispatch(fetchGrantedDelegations({}));
      dispatch(fetchMyPermissions());
    } catch (err) {
      setError(err.error || 'Failed to create delegation');
    }
  };

  const handleApproveDelegation = async (delegationId) => {
    try {
      await dispatch(approveDelegation(delegationId)).unwrap();
      flash('Delegation approved');
      dispatch(fetchPendingDelegations());
      dispatch(fetchMyPermissions());
    } catch (err) {
      setError(err.error || 'Failed to approve delegation');
    }
  };

  const handleRejectDelegation = (delegationId) => {
    askConfirm(
      'Reject delegation', 'Reject this pending delegation?', 'Reject', 'warn',
      async () => {
        try {
          await dispatch(rejectDelegation({ delegationId, reason: 'Rejected by manager' })).unwrap();
          flash('Delegation rejected');
          dispatch(fetchPendingDelegations());
        } catch (err) {
          setError(err.error || 'Failed to reject delegation');
        }
      }
    );
  };

  const handleRevokeDelegation = (delegationId) => {
    askConfirm(
      'Revoke delegation',
      'Revoke this delegation? The delegated permissions are removed immediately.',
      'Revoke', 'warn',
      async () => {
        try {
          await dispatch(revokeDelegation({ delegationId, reason: 'Revoked by grantor' })).unwrap();
          flash('Delegation revoked');
          dispatch(fetchGrantedDelegations({}));
          dispatch(fetchMyPermissions());
        } catch (err) {
          setError(err.error || 'Failed to revoke delegation');
        }
      }
    );
  };

  const handleInitializeRoles = async () => {
    try {
      await dispatch(initializeDefaultRoles()).unwrap();
      flash('Default roles initialized');
      dispatch(fetchRoles({ includePermissions: true }));
    } catch (err) {
      setError(err.error || 'Failed to initialize roles');
    }
  };

  const handleSeedPermissions = async () => {
    try {
      setLoading(true);
      await dispatch(seedPermissions()).unwrap();
      flash('System permissions initialized');
      dispatch(fetchPermissionsByCategory());
      dispatch(fetchRoles({ includePermissions: true }));
    } catch (err) {
      setError(err.error || 'Failed to initialize permissions');
    } finally {
      setLoading(false);
    }
  };

  const openEditRoleModal = (member) => {
    setSelectedMember(member);
    setInviteForm({ email: member.email, roleId: member.roleId || '' });
    setShowEditRoleModal(true);
  };

  const openEditPermissionsModal = (role) => {
    setSelectedRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || '',
      color: role.color || '#0D7463',
    });
    setSelectedPermissions(role.permissions?.map((p) => p.permissionId || p.id) || []);
    setShowEditPermissionsModal(true);
  };

  const openMemberDetailsModal = (member) => {
    setSelectedMember(member);
    setShowMemberDetailsModal(true);
  };

  const togglePermission = (permissionId) => {
    setSelectedPermissions((prev) =>
      prev.includes(permissionId) ? prev.filter((id) => id !== permissionId) : [...prev, permissionId]
    );
  };

  const toggleCategoryPermissions = (categoryPermissions) => {
    const categoryIds = categoryPermissions.map((p) => p.id);
    const allSelected = categoryIds.every((id) => selectedPermissions.includes(id));
    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((id) => !categoryIds.includes(id)));
    } else {
      setSelectedPermissions((prev) => [...new Set([...prev, ...categoryIds])]);
    }
  };

  const getRoleForMember = (member) => {
    if (member.roleId) return roles.find((r) => r.id === member.roleId);
    return roles.find((r) => r.name.toUpperCase() === member.merchantRole?.toUpperCase());
  };

  const stats = useMemo(() => ({
    totalMembers: teamMembers.length,
    pendingInvites: pendingInvites.length,
    totalRoles: roles.length,
    pendingApprovals: pendingDelegations.length,
  }), [teamMembers, pendingInvites, roles, pendingDelegations]);

  /* ─── Render ────────────────────────────────────────────────── */
  return (
    <Shell>
      <FeatureGate featureId="team.management" upgradeMessage="Upgrade to access team management and collaboration features">
        <div className="tm-page">

          {/* ── Header ───────────────────────────────────────────── */}
          <header className="tm-header">
            <div className="tm-header-text">
              <h1 className="tm-title">Team</h1>
              <p className="tm-subtitle">Invite members, manage roles, and delegate permissions.</p>
            </div>
            <div className="tm-header-actions">
              {roles.length === 0 && canManageRoles && (
                <button type="button" className="tm-btn tm-btn-secondary" onClick={handleInitializeRoles}>
                  <Shield size={14} /> <span className="tm-btn-text">Initialize roles</span>
                </button>
              )}
              {canInviteMembers && (
                <button
                  type="button"
                  className="tm-btn tm-btn-primary"
                  onClick={() => setShowInviteModal(true)}
                  disabled={teamLimits && !teamLimits.unlimited && teamLimits.current >= teamLimits.max}
                >
                  <UserPlus size={14} /> <span className="tm-btn-text">Invite member</span>
                </button>
              )}
            </div>
          </header>

          {/* ── Tabs ─────────────────────────────────────────────── */}
          <div className="tm-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.MEMBERS}
              className={`tm-tab${activeTab === TABS.MEMBERS ? ' is-active' : ''}`}
              onClick={() => setActiveTab(TABS.MEMBERS)}
            >
              Members
              {stats.totalMembers > 0 && <span className="tm-tab-badge">{stats.totalMembers}</span>}
            </button>
            {canManageRoles && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === TABS.ROLES}
                className={`tm-tab${activeTab === TABS.ROLES ? ' is-active' : ''}`}
                onClick={() => setActiveTab(TABS.ROLES)}
              >
                Roles
                {stats.totalRoles > 0 && <span className="tm-tab-badge">{stats.totalRoles}</span>}
              </button>
            )}
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.DELEGATIONS}
              className={`tm-tab${activeTab === TABS.DELEGATIONS ? ' is-active' : ''}`}
              onClick={() => setActiveTab(TABS.DELEGATIONS)}
            >
              Delegations
              {stats.pendingApprovals > 0 && <span className="tm-tab-badge tm-tab-badge-warn">{stats.pendingApprovals}</span>}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === TABS.ACTIVITY}
              className={`tm-tab${activeTab === TABS.ACTIVITY ? ' is-active' : ''}`}
              onClick={() => setActiveTab(TABS.ACTIVITY)}
            >
              Activity
            </button>
          </div>

          {/* ── Banners ──────────────────────────────────────────── */}
          {error && (
            <div className="tm-banner tm-banner-error">
              <AlertTriangle size={16} />
              <span>{error}</span>
              <button type="button" className="tm-banner-close" onClick={() => setError('')} aria-label="Dismiss">×</button>
            </div>
          )}
          {success && (
            <div className="tm-banner tm-banner-success">
              <CheckCircle2 size={16} />
              <span>{success}</span>
              <button type="button" className="tm-banner-close" onClick={() => setSuccess('')} aria-label="Dismiss">×</button>
            </div>
          )}

          {/* Tier usage */}
          {teamLimits && !teamLimits.unlimited && (
            <div className="tm-tier-wrap">
              <div className="tm-tier">
                <span className="tm-tier-text">
                  <strong>{teamLimits.current}</strong> of {teamLimits.max} team members
                </span>
                <div className="tm-tier-track">
                  <div className="tm-tier-fill" style={{ width: `${Math.min(100, (teamLimits.current / teamLimits.max) * 100)}%` }} />
                </div>
              </div>
              {teamLimits.current >= teamLimits.max && (
                <div className="tm-banner tm-banner-warn">
                  <AlertTriangle size={16} />
                  <span>
                    You've hit the {teamLimits.max}-member limit for your {teamLimits.tier} plan.{' '}
                    <a href="/settings?tab=billing" className="tm-banner-link">Upgrade</a> to add more.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* ── Members tab ──────────────────────────────────────── */}
          {activeTab === TABS.MEMBERS && (
            <MembersTab
              teamMembers={teamMembers}
              pendingInvites={pendingInvites}
              roles={roles}
              loading={loading}
              canManageTeam={canManageTeam}
              onEditRole={openEditRoleModal}
              onRemoveMember={handleRemoveMember}
              onCancelInvite={handleCancelInvite}
              onViewDetails={openMemberDetailsModal}
              getRoleForMember={getRoleForMember}
              stats={stats}
            />
          )}

          {/* ── Roles tab ────────────────────────────────────────── */}
          {activeTab === TABS.ROLES && canManageRoles && (
            <RolesTab
              roles={roles}
              loading={rolesStatus === 'loading'}
              onCreateRole={() => {
                setRoleForm({ name: '', description: '', color: '#0D7463' });
                setSelectedPermissions([]);
                setShowCreateRoleModal(true);
              }}
              onEditPermissions={openEditPermissionsModal}
              onDeleteRole={handleDeleteRole}
              onCloneRole={handleCloneRole}
            />
          )}

          {/* ── Delegations tab ──────────────────────────────────── */}
          {activeTab === TABS.DELEGATIONS && (
            <DelegationsTab
              pendingDelegations={pendingDelegations}
              grantedDelegations={grantedDelegations}
              receivedDelegations={receivedDelegations}
              loading={delegationsStatus === 'loading'}
              isOwner={isOwner}
              onCreateDelegation={() => setShowDelegateModal(true)}
              onApprove={handleApproveDelegation}
              onReject={handleRejectDelegation}
              onRevoke={handleRevokeDelegation}
            />
          )}

          {/* ── Activity tab ─────────────────────────────────────── */}
          {activeTab === TABS.ACTIVITY && (
            <ActivityTab activityLogs={activityLogs} loading={loading} />
          )}

          {/* ─────────── Modals ─────────── */}

          {/* Invite */}
          <Modal
            isOpen={showInviteModal}
            onClose={() => setShowInviteModal(false)}
            title="Invite team member"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="tm-btn tm-btn-secondary" onClick={() => setShowInviteModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="tm-invite-form"
                  className="tm-btn tm-btn-primary"
                  disabled={!inviteForm.email || !inviteForm.roleId || loading}
                >
                  {loading ? <Loader2 size={14} className="tm-spin" /> : <Send size={14} />}
                  Send invite
                </button>
              </>
            }
          >
            <form id="tm-invite-form" onSubmit={handleInviteMember} className="tm-form">
              <div className="tm-field">
                <label className="tm-field-label">Email</label>
                <input
                  type="email"
                  className="tm-input"
                  placeholder="colleague@example.com"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                  required
                />
              </div>
              <div className="tm-field">
                <label className="tm-field-label">Role</label>
                <RoleRadioList
                  roles={roles}
                  selectedRoleId={inviteForm.roleId}
                  onSelect={(id) => setInviteForm((p) => ({ ...p, roleId: id }))}
                />
              </div>
            </form>
          </Modal>

          {/* Edit member role */}
          <Modal
            isOpen={showEditRoleModal}
            onClose={() => setShowEditRoleModal(false)}
            title="Change role"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="tm-btn tm-btn-secondary" onClick={() => setShowEditRoleModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="tm-edit-role-form"
                  className="tm-btn tm-btn-primary"
                  disabled={!inviteForm.roleId || loading}
                >
                  {loading ? <Loader2 size={14} className="tm-spin" /> : null}
                  Update role
                </button>
              </>
            }
          >
            <form id="tm-edit-role-form" onSubmit={handleUpdateMemberRole} className="tm-form">
              {/* Member context — used to live in the modal title but long
                  emails truncated with ellipsis on narrow viewports. */}
              {selectedMember && (
                <div className="tm-context">
                  <div className="tm-context-label">Member</div>
                  <div className="tm-context-value">{selectedMember.username || selectedMember.email}</div>
                </div>
              )}
              <RoleRadioList
                roles={roles}
                selectedRoleId={inviteForm.roleId}
                onSelect={(id) => setInviteForm((p) => ({ ...p, roleId: id }))}
              />
            </form>
          </Modal>

          {/* Create role */}
          <Modal
            isOpen={showCreateRoleModal}
            onClose={() => setShowCreateRoleModal(false)}
            title="Create custom role"
            size="lg"
            padded={false}
            footer={
              <>
                <button type="button" className="tm-btn tm-btn-secondary" onClick={() => setShowCreateRoleModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="tm-create-role-form"
                  className="tm-btn tm-btn-primary"
                  disabled={!roleForm.name}
                >
                  Create role
                </button>
              </>
            }
          >
            <form id="tm-create-role-form" onSubmit={handleCreateRole} className="tm-form">
              <div className="tm-form-row">
                <div className="tm-field">
                  <label className="tm-field-label">Name</label>
                  <input
                    type="text"
                    className="tm-input"
                    placeholder="Senior Manager"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm((p) => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="tm-field">
                  <label className="tm-field-label">Color</label>
                  <ColorSwatchPicker
                    value={roleForm.color}
                    onChange={(color) => setRoleForm((p) => ({ ...p, color }))}
                  />
                </div>
              </div>
              <div className="tm-field">
                <label className="tm-field-label">Description</label>
                <textarea
                  className="tm-input tm-textarea"
                  placeholder="What can this role do?"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="tm-field">
                <label className="tm-field-label">
                  Permissions {selectedPermissions.length > 0 && `(${selectedPermissions.length} selected)`}
                </label>
                <PermissionSelector
                  permissionsByCategory={permissionsByCategory}
                  selectedPermissions={selectedPermissions}
                  onTogglePermission={togglePermission}
                  onToggleCategory={toggleCategoryPermissions}
                  onSeedPermissions={handleSeedPermissions}
                  isLoading={loading}
                />
              </div>
            </form>
          </Modal>

          {/* Edit role permissions */}
          <Modal
            isOpen={showEditPermissionsModal}
            onClose={() => setShowEditPermissionsModal(false)}
            title={selectedRole ? `Edit role — ${selectedRole.name}` : 'Edit role'}
            size="lg"
            padded={false}
            footer={
              <>
                <button type="button" className="tm-btn tm-btn-secondary" onClick={() => setShowEditPermissionsModal(false)}>
                  Cancel
                </button>
                <button type="button" className="tm-btn tm-btn-primary" onClick={handleUpdateRolePermissions}>
                  Save
                </button>
              </>
            }
          >
            <div className="tm-form">
              {selectedRole?.isSystem && (
                <div className="tm-banner tm-banner-info">
                  <AlertTriangle size={14} />
                  <span>System role — permissions can change but the role can't be deleted.</span>
                </div>
              )}
              <div className="tm-form-row">
                <div className="tm-field">
                  <label className="tm-field-label">Name</label>
                  <input
                    type="text"
                    className="tm-input"
                    value={roleForm.name}
                    onChange={(e) => setRoleForm((p) => ({ ...p, name: e.target.value }))}
                    disabled={selectedRole?.isSystem}
                  />
                </div>
                <div className="tm-field">
                  <label className="tm-field-label">Color</label>
                  <ColorSwatchPicker
                    value={roleForm.color}
                    onChange={(color) => setRoleForm((p) => ({ ...p, color }))}
                  />
                </div>
              </div>
              <div className="tm-field">
                <label className="tm-field-label">Description</label>
                <textarea
                  className="tm-input tm-textarea"
                  value={roleForm.description}
                  onChange={(e) => setRoleForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="tm-field">
                <label className="tm-field-label">
                  Permissions ({selectedPermissions.length} selected)
                </label>
                <PermissionSelector
                  permissionsByCategory={permissionsByCategory}
                  selectedPermissions={selectedPermissions}
                  onTogglePermission={togglePermission}
                  onToggleCategory={toggleCategoryPermissions}
                  onSeedPermissions={handleSeedPermissions}
                  isLoading={loading}
                />
              </div>
            </div>
          </Modal>

          {/* Delegate permissions */}
          <Modal
            isOpen={showDelegateModal}
            onClose={() => setShowDelegateModal(false)}
            title="Delegate permissions"
            size="md"
            padded={false}
            footer={
              <>
                <button type="button" className="tm-btn tm-btn-secondary" onClick={() => setShowDelegateModal(false)}>
                  Cancel
                </button>
                <button
                  type="submit"
                  form="tm-delegate-form"
                  className="tm-btn tm-btn-primary"
                  disabled={!delegationForm.delegateToId || delegationForm.permissionIds.length === 0 || !delegationForm.expiresAt}
                >
                  <Send size={14} /> Create delegation
                </button>
              </>
            }
          >
            <form id="tm-delegate-form" onSubmit={handleCreateDelegation} className="tm-form">
              <div className="tm-field">
                <label className="tm-field-label">Delegate to</label>
                <select
                  className="tm-input tm-select"
                  value={delegationForm.delegateToId}
                  onChange={(e) => setDelegationForm((p) => ({ ...p, delegateToId: e.target.value }))}
                  required
                >
                  <option value="">Select team member…</option>
                  {teamMembers.filter((m) => m.id !== user?.id).map((m) => (
                    <option key={m.id} value={m.id}>{m.username || m.email}</option>
                  ))}
                </select>
              </div>
              <div className="tm-field">
                <label className="tm-field-label">Permissions to delegate</label>
                {delegablePermissions.length === 0 ? (
                  <div className="tm-empty-inline">No permissions available to delegate.</div>
                ) : (
                  <ul className="tm-perm-list">
                    {delegablePermissions.map((perm) => (
                      <li key={perm.id}>
                        <label className="tm-perm-row">
                          <input
                            type="checkbox"
                            checked={delegationForm.permissionIds.includes(perm.id)}
                            onChange={(e) => {
                              setDelegationForm((p) => ({
                                ...p,
                                permissionIds: e.target.checked
                                  ? [...p.permissionIds, perm.id]
                                  : p.permissionIds.filter((id) => id !== perm.id),
                              }));
                            }}
                          />
                          <span className="tm-perm-name">{perm.name}</span>
                          <span className="tm-perm-code">{perm.code}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="tm-form-row">
                <div className="tm-field">
                  <label className="tm-field-label">Expires</label>
                  <input
                    type="datetime-local"
                    className="tm-input"
                    value={delegationForm.expiresAt}
                    onChange={(e) => setDelegationForm((p) => ({ ...p, expiresAt: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div className="tm-field">
                <label className="tm-field-label">Reason (optional)</label>
                <textarea
                  className="tm-input tm-textarea"
                  placeholder="Why are you delegating?"
                  value={delegationForm.reason}
                  onChange={(e) => setDelegationForm((p) => ({ ...p, reason: e.target.value }))}
                  rows={2}
                />
              </div>
              <div className="tm-toggle-row">
                <div className="tm-toggle-text">
                  <span className="tm-toggle-label">Require approval</span>
                  <span className="tm-toggle-sub">
                    {delegationForm.requiresApproval
                      ? 'Will sit pending until an owner approves.'
                      : 'Active as soon as you create it.'}
                  </span>
                </div>
                <label className="tm-toggle">
                  <input
                    type="checkbox"
                    checked={delegationForm.requiresApproval}
                    onChange={(e) => setDelegationForm((p) => ({ ...p, requiresApproval: e.target.checked }))}
                  />
                  <span className="tm-toggle-track" />
                </label>
              </div>
            </form>
          </Modal>

          {/* Member details */}
          <Modal
            isOpen={showMemberDetailsModal}
            onClose={() => setShowMemberDetailsModal(false)}
            title={selectedMember ? `${selectedMember.username || selectedMember.email}` : 'Member'}
            size="md"
            padded={false}
          >
            {selectedMember && (() => {
              const role = getRoleForMember(selectedMember);
              const color = role?.color || getColorForRole(role?.name || 'Viewer');
              const initials = (selectedMember.username || selectedMember.email).charAt(0).toUpperCase();
              return (
                <div className="tm-detail">
                  <div className="tm-detail-head">
                    <div className="tm-detail-avatar" style={{ background: color }}>{initials}</div>
                    <div className="tm-detail-text">
                      <h3 className="tm-detail-name">{selectedMember.username || 'No username'}</h3>
                      <p className="tm-detail-email">{selectedMember.email}</p>
                      <span className="tm-role-badge" style={{ background: `${color}1A`, color }}>
                        {role?.name === 'Owner' && <Crown size={11} />}
                        {role?.name || selectedMember.merchantRole || 'No role'}
                      </span>
                    </div>
                  </div>

                  <dl className="tm-detail-kv">
                    <div className="tm-detail-kv-row">
                      <dt>Status</dt>
                      <dd>
                        <span className={`tm-pill ${selectedMember.isActive ? 'tm-pill-success' : 'tm-pill-muted'}`}>
                          {selectedMember.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </dd>
                    </div>
                    <div className="tm-detail-kv-row">
                      <dt>Joined</dt>
                      <dd>{new Date(selectedMember.createdAt).toLocaleDateString()}</dd>
                    </div>
                  </dl>

                  <div className="tm-detail-section">
                    <h4 className="tm-detail-section-title">Effective permissions</h4>
                    {(role?.permissions || []).length === 0 ? (
                      <p className="tm-empty-inline">No permissions assigned.</p>
                    ) : (
                      <ul className="tm-perm-readonly">
                        {role.permissions.map((perm, idx) => (
                          <li key={idx}>
                            <CheckCircle2 size={12} />
                            <span>{perm.permission?.name || perm.name || perm.code}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })()}
          </Modal>

          {/* Confirmation modal (replaces old createPortal DeleteConfirmModal) */}
          <Modal
            isOpen={!!confirm}
            onClose={() => setConfirm(null)}
            title={confirm?.title || ''}
            size="sm"
            padded={false}
            footer={confirm && (
              <>
                <button type="button" className="tm-btn tm-btn-secondary" onClick={() => setConfirm(null)}>
                  Cancel
                </button>
                <button
                  type="button"
                  className={`tm-btn ${confirm.type === 'danger' ? 'tm-btn-danger-solid' : 'tm-btn-warn-solid'}`}
                  onClick={confirm.onConfirm}
                >
                  {confirm.confirmText}
                </button>
              </>
            )}
          >
            {confirm && (
              <div className="tm-confirm">
                <AlertTriangle
                  size={20}
                  className={confirm.type === 'danger' ? 'tm-confirm-icon-danger' : 'tm-confirm-icon-warn'}
                />
                <p>{confirm.message}</p>
              </div>
            )}
          </Modal>

        </div>
      </FeatureGate>
    </Shell>
  );
}

/* ─── Sub-components ─────────────────────────────────────────── */

function MembersTab({
  teamMembers, pendingInvites, roles, loading, canManageTeam,
  onEditRole, onRemoveMember, onCancelInvite, onViewDetails,
  getRoleForMember, stats,
}) {
  if (loading) return <div className="tm-loading"><Loader2 size={20} className="tm-spin" /> Loading…</div>;

  return (
    <>
      {/* 3-up stat tiles */}
      <section className="tm-stat-grid">
        <div className="tm-stat">
          <span className="tm-stat-label">Members</span>
          <span className="tm-stat-value">{stats.totalMembers}</span>
        </div>
        <div className="tm-stat">
          <span className="tm-stat-label">Pending</span>
          <span className="tm-stat-value tm-stat-value-warn">{stats.pendingInvites}</span>
        </div>
        <div className="tm-stat">
          <span className="tm-stat-label">Roles</span>
          <span className="tm-stat-value">{stats.totalRoles}</span>
        </div>
      </section>

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <section className="tm-section">
          <h2 className="tm-section-title">Pending invitations</h2>
          <ul className="tm-list">
            {pendingInvites.map((invite) => {
              const role = roles.find((r) => r.id === invite.roleId) || { name: invite.role, color: '#6B7280' };
              const color = role.color || getColorForRole(role.name);
              return (
                <li key={invite.id} className="tm-row">
                  <div className="tm-row-main tm-invite">
                    <span className="tm-invite-icon" style={{ background: `${color}1A`, color }}>
                      <Mail size={14} />
                    </span>
                    <div className="tm-invite-info">
                      <div className="tm-invite-email">{invite.email}</div>
                      <div className="tm-invite-meta">
                        <span className="tm-pill" style={{ background: `${color}1A`, color }}>{role.name}</span>
                        <span className="tm-invite-time">
                          Invited {new Date(invite.createdAt).toLocaleDateString()}
                          {invite.expiresAt && ` · expires ${new Date(invite.expiresAt).toLocaleDateString()}`}
                        </span>
                      </div>
                    </div>
                    {canManageTeam && (
                      <button
                        type="button"
                        className="tm-btn tm-btn-ghost"
                        onClick={() => onCancelInvite(invite.id)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/* Members list */}
      <section className="tm-section">
        <h2 className="tm-section-title">Members</h2>
        {teamMembers.length === 0 ? (
          <div className="tm-empty">
            <div className="tm-empty-icon"><Users size={22} /></div>
            <h3 className="tm-empty-title">No team members yet</h3>
            <p className="tm-empty-desc">Invite your first team member to get started.</p>
          </div>
        ) : (
          <ul className="tm-list">
            {teamMembers.map((member) => {
              const role = getRoleForMember(member);
              const color = role?.color || getColorForRole(role?.name || member.merchantRole);
              const isOwnerMember = member.merchantRole === 'OWNER' || role?.name === 'Owner';
              const initials = (member.username || member.email).charAt(0).toUpperCase();
              return (
                <li key={member.id} className="tm-row">
                  <div className="tm-row-main tm-member">
                    <div className="tm-member-avatar" style={{ background: color }}>{initials}</div>
                    <div className="tm-member-info">
                      <div className="tm-member-name">{member.username || 'Unnamed'}</div>
                      <div className="tm-member-email">{member.email}</div>
                    </div>
                    {/* Badges wrapper — flex on mobile so the role badge + status pill
                        sit side-by-side instead of stacking in the same grid cell. */}
                    <div className="tm-member-badges">
                      <span className="tm-role-badge" style={{ background: `${color}1A`, color }}>
                        {isOwnerMember && <Crown size={11} />}
                        {role?.name || member.merchantRole || 'No role'}
                      </span>
                      <span className={`tm-pill ${member.isActive ? 'tm-pill-success' : 'tm-pill-muted'}`}>
                        {member.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <span className="tm-member-joined">{new Date(member.createdAt).toLocaleDateString()}</span>
                    <div className="tm-member-actions">
                      <button
                        type="button"
                        className="tm-icon-btn"
                        onClick={() => onViewDetails(member)}
                        title="View details"
                        aria-label="View details"
                      >
                        <Eye size={13} />
                      </button>
                      {!isOwnerMember && canManageTeam && (
                        <>
                          <button
                            type="button"
                            className="tm-icon-btn"
                            onClick={() => onEditRole(member)}
                            title="Edit role"
                            aria-label="Edit role"
                          >
                            <Edit3 size={13} />
                          </button>
                          <button
                            type="button"
                            className="tm-icon-btn tm-icon-btn-danger"
                            onClick={() => onRemoveMember(member.id, member.email)}
                            title="Remove member"
                            aria-label="Remove member"
                          >
                            <Trash2 size={13} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </>
  );
}

function RolesTab({ roles, loading, onCreateRole, onEditPermissions, onDeleteRole, onCloneRole }) {
  if (loading) return <div className="tm-loading"><Loader2 size={20} className="tm-spin" /> Loading…</div>;

  return (
    <>
      <div className="tm-section-bar">
        <p className="tm-section-bar-text">Roles bundle permissions. Assign one to a member from the Members tab.</p>
        <button type="button" className="tm-btn tm-btn-primary" onClick={onCreateRole}>
          <Plus size={14} /> Create role
        </button>
      </div>

      {roles.length === 0 ? (
        <div className="tm-empty">
          <div className="tm-empty-icon"><Shield size={22} /></div>
          <h2 className="tm-empty-title">No roles yet</h2>
          <p className="tm-empty-desc">Create custom roles to manage team permissions.</p>
          <button type="button" className="tm-btn tm-btn-primary" onClick={onCreateRole}>
            <Plus size={14} /> Create your first role
          </button>
        </div>
      ) : (
        <div className="tm-role-grid">
          {roles.map((role) => {
            const color = role.color || getColorForRole(role.name);
            return (
              <article key={role.id} className="tm-role-card">
                <div className="tm-role-stripe" style={{ background: color }} />
                <div className="tm-role-body">
                  <div className="tm-role-head">
                    <h3 className="tm-role-name">{role.name}</h3>
                    <div className="tm-role-tags">
                      {role.isSystem && <span className="tm-pill tm-pill-muted">System</span>}
                      {role.isDefault && <span className="tm-pill tm-pill-info">Default</span>}
                    </div>
                  </div>
                  <p className="tm-role-desc">{role.description || 'No description'}</p>
                  <div className="tm-role-stats">
                    <span>{role.permissions?.length || 0} permissions</span>
                    <span>·</span>
                    <span>{role._count?.teamMembers || 0} members</span>
                  </div>
                  <div className="tm-role-actions">
                    <button type="button" className="tm-btn tm-btn-ghost" onClick={() => onEditPermissions(role)}>
                      <Edit3 size={13} /> Edit
                    </button>
                    <button type="button" className="tm-btn tm-btn-ghost" onClick={() => onCloneRole(role)}>
                      <Copy size={13} /> Clone
                    </button>
                    {!role.isSystem && (
                      <button type="button" className="tm-btn tm-btn-danger" onClick={() => onDeleteRole(role)}>
                        <Trash2 size={13} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </>
  );
}

function DelegationsTab({
  pendingDelegations, grantedDelegations, receivedDelegations,
  loading, isOwner, onCreateDelegation, onApprove, onReject, onRevoke,
}) {
  if (loading) return <div className="tm-loading"><Loader2 size={20} className="tm-spin" /> Loading…</div>;

  const isEmpty =
    pendingDelegations.length === 0 &&
    grantedDelegations.length === 0 &&
    receivedDelegations.length === 0;

  return (
    <>
      <div className="tm-section-bar">
        <p className="tm-section-bar-text">Temporarily grant specific permissions to a team member.</p>
        <button type="button" className="tm-btn tm-btn-primary" onClick={onCreateDelegation}>
          <Send size={14} /> Delegate
        </button>
      </div>

      {isEmpty ? (
        <div className="tm-empty">
          <div className="tm-empty-icon"><Send size={22} /></div>
          <h2 className="tm-empty-title">No delegations yet</h2>
          <p className="tm-empty-desc">Delegate permissions when a team member needs temporary access.</p>
        </div>
      ) : (
        <>
          {isOwner && pendingDelegations.length > 0 && (
            <DelegationSection
              title="Pending your approval"
              icon={Clock}
              tone="warn"
              delegations={pendingDelegations}
              showParties={true}
              actions={(d) => (
                <>
                  <button type="button" className="tm-btn tm-btn-success" onClick={() => onApprove(d.id)}>Approve</button>
                  <button type="button" className="tm-btn tm-btn-danger" onClick={() => onReject(d.id)}>Reject</button>
                </>
              )}
            />
          )}

          {grantedDelegations.length > 0 && (
            <DelegationSection
              title="Granted by you"
              icon={Send}
              delegations={grantedDelegations}
              actions={(d) => d.status === 'ACTIVE' ? (
                <button type="button" className="tm-btn tm-btn-ghost" onClick={() => onRevoke(d.id)}>Revoke</button>
              ) : null}
              showTo
            />
          )}

          {receivedDelegations.length > 0 && (
            <DelegationSection
              title="Granted to you"
              icon={CheckCircle2}
              delegations={receivedDelegations}
              showFrom
            />
          )}
        </>
      )}
    </>
  );
}

function DelegationSection({ title, icon: Icon, tone, delegations, actions, showParties, showFrom, showTo }) {
  return (
    <section className="tm-section">
      <h2 className={`tm-section-title${tone === 'warn' ? ' is-warn' : ''}`}>
        <Icon size={14} />
        {title}
        <span className="tm-section-count">({delegations.length})</span>
      </h2>
      <ul className="tm-list">
        {delegations.map((d) => {
          const statusPill =
            d.status === 'ACTIVE' ? 'tm-pill-success' :
            d.status === 'PENDING' ? 'tm-pill-warn' :
            d.status === 'EXPIRED' ? 'tm-pill-muted' :
            d.status === 'REJECTED' ? 'tm-pill-danger' :
            d.status === 'REVOKED' ? 'tm-pill-muted' : 'tm-pill-info';
          return (
            <li key={d.id} className="tm-row">
              <div className="tm-row-main tm-delegation">
                <div className="tm-delegation-head">
                  {(showParties || showFrom) && (
                    <span className="tm-delegation-party">
                      <span className="tm-delegation-party-label">From</span>
                      <span>{d.grantor?.username || d.grantor?.email || '—'}</span>
                    </span>
                  )}
                  {(showParties || showTo) && (
                    <span className="tm-delegation-party">
                      <span className="tm-delegation-party-label">{showParties ? '→ To' : 'To'}</span>
                      <span>{d.delegate?.username || d.delegate?.email || '—'}</span>
                    </span>
                  )}
                  <span className={`tm-pill ${statusPill}`}>{d.status}</span>
                </div>
                {d.permissions?.length > 0 && (
                  <div className="tm-delegation-perms">
                    {d.permissions.map((p, i) => (
                      <span key={i} className="tm-pill tm-pill-muted">{p.permission?.name || p.name}</span>
                    ))}
                  </div>
                )}
                <div className="tm-delegation-meta">
                  <span>Expires {new Date(d.expiresAt).toLocaleDateString()}</span>
                  {d.reason && <span>· {d.reason}</span>}
                </div>
                {actions && (
                  <div className="tm-delegation-actions">{actions(d)}</div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function ActivityTab({ activityLogs, loading }) {
  if (loading) return <div className="tm-loading"><Loader2 size={20} className="tm-spin" /> Loading…</div>;

  const formatAction = (action) =>
    action ? action.replace(/[._]/g, ' ').toLowerCase() : 'event';

  if (activityLogs.length === 0) {
    return (
      <div className="tm-empty">
        <div className="tm-empty-icon"><ClipboardList size={22} /></div>
        <h2 className="tm-empty-title">No activity yet</h2>
        <p className="tm-empty-desc">Team actions (invites, role changes, delegations) will show up here.</p>
      </div>
    );
  }

  return (
    <ul className="tm-list">
      {activityLogs.map((activity, idx) => {
        const Icon = iconForActivity(activity.action);
        return (
          <li key={activity.id || idx} className="tm-row">
            <div className="tm-row-main tm-activity">
              <span className="tm-activity-icon"><Icon size={14} /></span>
              <div className="tm-activity-body">
                <p className="tm-activity-desc">
                  <strong>{activity.actor?.username || activity.actor?.email || 'System'}</strong>
                  {' '}{activity.description || formatAction(activity.action)}
                  {activity.teamMember?.user && (
                    <> — <strong>{activity.teamMember.user.username || activity.teamMember.user.email}</strong></>
                  )}
                </p>
                <span className="tm-activity-time">{new Date(activity.createdAt).toLocaleString()}</span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Permission selector ───────────────────────────────────── */
function PermissionSelector({ permissionsByCategory, selectedPermissions, onTogglePermission, onToggleCategory, onSeedPermissions, isLoading }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (category) => setExpanded((p) => ({ ...p, [category]: !p[category] }));
  const categories = Object.keys(permissionsByCategory);

  if (categories.length === 0) {
    return (
      <div className="tm-perm-empty">
        <p className="tm-empty-inline">No permissions available.</p>
        {onSeedPermissions && (
          <button type="button" className="tm-btn tm-btn-secondary" onClick={onSeedPermissions} disabled={isLoading}>
            {isLoading ? <Loader2 size={14} className="tm-spin" /> : null}
            Initialize system permissions
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="tm-perm-selector">
      {categories.map((category) => {
        const permissions = permissionsByCategory[category] || [];
        const selectedCount = permissions.filter((p) => selectedPermissions.includes(p.id)).length;
        const allSelected = selectedCount === permissions.length && permissions.length > 0;
        const isOpen = !!expanded[category];
        const Icon = CATEGORY_ICONS[category] || Package;
        return (
          <div key={category} className={`tm-perm-cat${isOpen ? ' is-open' : ''}`}>
            <button
              type="button"
              className="tm-perm-cat-head"
              onClick={() => toggle(category)}
              aria-expanded={isOpen}
            >
              <Icon size={14} className="tm-perm-cat-icon" />
              <span className="tm-perm-cat-name">{category}</span>
              <span className="tm-perm-cat-count">{selectedCount}/{permissions.length}</span>
              <span
                role="button"
                tabIndex={0}
                className={`tm-perm-cat-all${allSelected ? ' is-all' : ''}`}
                onClick={(e) => { e.stopPropagation(); onToggleCategory(permissions); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onToggleCategory(permissions); } }}
              >
                {allSelected ? 'Deselect all' : 'Select all'}
              </span>
              {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
            {isOpen && (
              <ul className="tm-perm-list">
                {permissions.map((permission) => (
                  <li key={permission.id}>
                    <label className="tm-perm-row">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.id)}
                        onChange={() => onTogglePermission(permission.id)}
                      />
                      <span className="tm-perm-name">{permission.name}</span>
                      <span className="tm-perm-code">{permission.code}</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Role radio list (used in Invite + Edit role modals) ──── */
function RoleRadioList({ roles, selectedRoleId, onSelect }) {
  const list = roles.filter((r) => r.name !== 'Owner');
  if (list.length === 0) {
    return <div className="tm-empty-inline">No roles defined. Initialize default roles first.</div>;
  }
  return (
    <div className="tm-radio-list" role="radiogroup">
      {list.map((role) => {
        const active = selectedRoleId === role.id;
        const color = role.color || getColorForRole(role.name);
        const permCount = role.permissions?.length || 0;
        return (
          <button
            key={role.id}
            type="button"
            role="radio"
            aria-checked={active}
            className={`tm-radio-row${active ? ' is-active' : ''}`}
            style={{ '--role-color': color }}
            onClick={() => onSelect(role.id)}
          >
            <span className="tm-radio-dot" />
            <span className="tm-radio-body">
              <span className="tm-radio-label">
                <span className="tm-radio-chip" style={{ background: color }} aria-hidden="true" />
                {role.name}
              </span>
              <span className="tm-radio-desc">
                {role.description || `${permCount} permission${permCount === 1 ? '' : 's'}`}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ─── Color swatch picker ──────────────────────────────────── */
function ColorSwatchPicker({ value, onChange }) {
  return (
    <div className="tm-swatches" role="radiogroup" aria-label="Role color">
      {ROLE_PALETTE.map((color) => (
        <button
          key={color}
          type="button"
          role="radio"
          aria-checked={value?.toUpperCase() === color.toUpperCase()}
          className={`tm-swatch${value?.toUpperCase() === color.toUpperCase() ? ' is-active' : ''}`}
          onClick={() => onChange(color)}
          style={{ background: color }}
          aria-label={color}
        />
      ))}
    </div>
  );
}
